import gql from 'graphql-tag';

import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend } = clients.direct;
const taxFields = {
  _id: '',
  taxYear: '',
  taxId: '',
  name1: '',
  name2: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  propertyOrParcelId: '',

};
const taxWarrantDetailsField = {
  _id: '',
  baseId: '',
  taxYear: '',
  taxId: '',
  totalTax: '',
  penaltyAmt: '',
  totalFee: '',
  totalDue: '',
  totalDueWithFeesPenalty: '',
  name1: '',
  name2: '',
};

const taxWarrantFields = {
  _id: '',
  systemId: '',
  baseId: '',
  taxYear: '',
  taxId: '',
  warrantNumber: '',
  aliasNumber: '',
  sherifFee: '',
  treasureFee: '',
  sherifFeeAmount: '',
  treasureFeeAmount: '',
  issueDate: '',
  servedDate: '',
  isActive: '',
  issueAmount: '',
  amountCollected: '',
  remarks: '',
  statusCode: '',
  renew: '',
  name1: '',
  name2: '',
  inCareOf1: '',
  inCareOf2: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  city: '',
  state: '',
  zip: '',
  taxWarrantDetails: taxWarrantDetailsField,
  taxInquiryTransaction: taxFields,
  penaltyDate: '',
  totalDue: '',
  totalFee: '',
  totalTax: '',
  formPrintedDate: '',
  letterPrintedDate: '',
};

export const taxWarrants = {
  namespaced: true,
  state: () => ({
    isDialog: false,
    selectedItems: [],
    nextWarrantNumber: null,
    nextWarrantSystemID: null,

  }),
  mutations: {
    setDialog(state, isDialog) {
      state.isDialog = isDialog;
    },
    setSelectedItems(state, items) {
      state.selectedItems = items;
    },
    setStats(state, results) {
      if (results.taxWarrantStatsMaxNo && results.taxWarrantStatsMaxNo.max !== undefined) {
        const nextWarrantNumber = results.taxWarrantStatsMaxNo.max + 1;
        if (state.nextWarrantNumber !== nextWarrantNumber) {
          state.nextWarrantNumber = nextWarrantNumber;
        }
      }
    },
    setSystemID(state, results) {
      if (results.taxWarrantSystemIDMaxNo && results.taxWarrantSystemIDMaxNo.max !== undefined) {
        const nextWarrantSystemID = results.taxWarrantSystemIDMaxNo.max + 1;
        if (state.nextWarrantSystemID !== nextWarrantSystemID) {
          state.nextWarrantSystemID = nextWarrantSystemID;
        }
      }
    },

  },
  getters: {
    getDialog(state) {
      return state.isDialog;
    },

  },
  actions: {
    async loadTaxWarrants({ commit, dispatch }, { criteria, baseCrudKey, postProcessTrans }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query taxWarrants($criteria: taxWarrantsCriteriaType) {
              taxWarrants(criteria: $criteria) {
                ${fieldsToGqlString(taxWarrantFields)},
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        if (baseCrudKey && results.data) {
          const taxData = postProcessTrans
            ? postProcessTrans(results.data.taxWarrants)
            || [] : results.data.taxWarrants;
          commit('base/crud/setItems', [baseCrudKey, taxData], { root: true });
        }
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting tax warrent transactions.', { root: true });
      }
      return results.data.taxWarrants;
    },
    /**
     * Which of the records about to be warranted carry a status code the county has
     * flagged "Exclude from Personal Tax Warrants" (bankruptcy and the like). Asked
     * before Create Warrants writes anything, so the user is told rather than finding
     * out when a discharged year turns up on a letter with a warrant number on it.
     *
     * Deliberately returns [] on failure: this is a warning, and losing it must not stop
     * the county issuing warrants.
     */
    async loadPtwExcludedTaxRecords({ commit, dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query ptwExcludedTaxRecords($criteria: ptwExcludedTaxRecordsCriteriaType) {
            ptwExcludedTaxRecords(criteria: $criteria) {
              baseId taxYear taxId name1 name2 propertyOrParcelId
              excludedStatusCodes excludedStatusCodeDescription isSelected
            }
          }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        return results.data.ptwExcludedTaxRecords || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        return [];
      }
    },
    async loadLastWarrantNo({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query taxWarrantStatsMaxNo {
            taxWarrantStatsMaxNo { max }
          }`,
          variables: {},
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        commit('setStats', results.data);
        return results.data.taxWarrantStatsMaxNo.max;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem determining the last payment number.', { root: true });
      }
      return {};
    },
    async loadLastSystemID({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query taxWarrantSystemIDMaxNo {
            taxWarrantSystemIDMaxNo { max }
          }`,
          variables: {},
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        commit('setSystemID', results.data);
        return results.data.taxWarrantSystemIDMaxNo.max;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem determining the last payment number.', { root: true });
      }
      return {};
    },
    // `silent: true` skips the success flash - callers that upsert many records in
    // one user action (e.g. a bulk penalty-date save) flash their own single
    // success message instead of stacking one toast per record.
    async upsertWarrants({ dispatch, rootGetters, commit }, { item, baseCrudKey, silent }) {
      let results;

      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertWarrants($item:taxWarrantsInputType) {
            upsertTaxWarrants(upsert:$item) {
              ${(fieldsToGqlString(taxWarrantFields))}
            }
          }`,
          variables: {
            item,
          },
        });

        clients.handleGQLErrors(results, commit);
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const newTaxWarrants = results.data.upsertTaxWarrants;
        const newList = mergeByIdNewAtTop(taxData || [], newTaxWarrants);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        if (!silent) dispatch('flashSuccess', 'Record added successfully', { root: true });
      } catch (error) {
        dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the tax warrant upsert', { root: true });
        throw error;
      }
    },
    async upsertBulkTaxWarrant({ dispatch }, { item }) {
      try {
        await dispatch('reAuth', null, { root: true });
        await backend.mutate({
          mutation: gql`mutation bulkUpsertTaxWarrant($item:[taxWarrantsInputType]) {
            bulkUpsertTaxWarrant(upserts:$item) {
              ${(fieldsToGqlString(taxWarrantFields))}
            }
          }`,
          variables: {
            item,
          },
        });
        dispatch('flashSuccess', 'Record added successfully', { root: true });
      } catch (error) {
        dispatch('logException', error, { root: true });
        // Surface the backend's own message - the warrant-number collision guard rejects
        // the whole batch and names the numbers already in use, which the generic text
        // would hide behind "there was a problem".
        const gqlMessage = (error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message)
          || error.message;
        dispatch('flashError', gqlMessage || 'There was a problem while upserting the Tax warrants transaction!', { root: true });
      }
    },
    async deleteTaxWarrants({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation bulkDeleteTaxWarrants ($ids: [ID!]!)  {
            bulkDeleteTaxWarrants(ids: $ids) {
              deletedCount
                }
              }`,
          variables: {
            ids: [item._id],
          },
        });
        clients.handleGQLErrors(results, commit);
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = taxData.filter((d) => d._id !== item._id);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        commit('base/crud/setIsVisible', [baseCrudKey, false], { root: true });
        dispatch('flashSuccess', 'Record deleted successfully', { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem deleting the Tax Warrant', { root: true });
      }
    },
    // Backend $pulls the taxWarrantDetails subdocument by its own _id and recomputes the
    // warrant's rollup totals (totalTax/totalFee/totalDue/issueAmount) from what remains, so
    // the returned warrant already reflects the row removal - callers just need to store it.
    async deleteTaxWarrantDetailRow({ commit, dispatch }, { deleteInfo }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteTaxWarrantDetailRow($deleteInfo: taxWarrantDetailRowDeleteInputType) {
            deleteTaxWarrantDetailRow(deleteInfo: $deleteInfo) {
              ${fieldsToGqlString(taxWarrantFields)}
            }
          }`,
          variables: { deleteInfo },
        });
        clients.handleGQLErrors(results, commit);
        dispatch('flashSuccess', 'Record deleted successfully', { root: true });
        return results.data.deleteTaxWarrantDetailRow;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem deleting the tax warrant detail record', { root: true });
      }
      return null;
    },
    async bulkDeleteTaxWarrants({ commit, rootGetters, dispatch }, { ids, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation bulkDeleteTaxWarrants ($ids: [ID!]!)  {
            bulkDeleteTaxWarrants(ids: $ids) {
              deletedCount
                }
              }`,
          variables: {
            ids,
          },
        });
        clients.handleGQLErrors(results, commit);
        const returnObj = results.data.bulkDeleteTaxWarrants;
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = taxData.filter((d) => !ids.includes(d._id));
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        dispatch('flashSuccess', `${returnObj.deletedCount} record(s) deleted successfully`, { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem deleting the Tax Warrants', { root: true });
      }
    },
  },
};

export default taxWarrants;
